import { Button } from "@/shared/ui/button.tsx";
import { GlowingEffect } from "@/shared/ui/glow-effect-border.tsx";
import { Check, ImagePlus, Plus, SquarePen, Upload, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { ChatMessageType, Platform, PostBodyFormSchema, PostBodyFormType, SocialPostType } from "../types";
import { format, isPast, isSameDay, startOfDay } from "date-fns";
// import GalleryDialog from "@/shared/gallery/GalleryDialog";
import { SetStateAction, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Input } from "@/shared/ui/input.tsx";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover.tsx"
import { Calendar } from "@/shared/ui/calendar.tsx";
import { cn } from "@/lib/utils";
import ImageDialog from "./theme-result/ImageDialog";
import PromptForm from "./theme-result/PromptForm";
import { useSaveImageTheme, useUpdatePostBody } from "../api";
import ConfirmScheduleDialog from "./ConfirmScheduleDialog";
import DateCheckDialog from "./DateCheckDialog";
import posthog from "posthog-js";
import { TimePicker } from "@/shared/ui/time-picker.tsx";
import MultiPlatformSelect from "./MultiPlatformSelect";
import { DateTime } from "luxon";
import PublishButton from "./PublishButton";
import { toast } from "sonner";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";
import GallerySelector from "@/shared/gallery-new";
import FeedbackPopover from "./theme-result/FeedbackPopover";
import ImageCarousel from "@/shared/ui/image-carousel";
import { useSearchParams } from "react-router-dom";
import EditTextDialog from "./dialogs/EditTextDialog";
import { Template, useGetTemplates } from "../api/templates";
import ImageUploadDialog from "./dialogs/ImageUploadDialog";

type Props = {
  story_id: number | null,
  data: SocialPostType,
  event_id: number,
  status: number,
  event_name: string,
  showPrompt: boolean,
  platform: { name: string, url: string },
  setShowPrompt: React.Dispatch<SetStateAction<boolean>>,
  setStep: (id: ChatMessageType) => void,
  filteredPlatforms: Platform[]
  setMessage: (text: string, client: string) => void
}

const getTemplateDetails = (id: number, data: Template[]): Template => {
  const foundIndex = data.findIndex(item => item.template_id === id);

  return data[foundIndex]

}

const ThemeDetailsCard = ({ data, event_name, showPrompt, platform, setShowPrompt, event_id, setMessage, setStep, filteredPlatforms, story_id, status }: Props) => {

  const [searchParams, _] = useSearchParams()

  const [currentImage, setCurrentImage] = useState(0)

  const [openTextEditor, setOpenTextEditor] = useState(false)

  const [openUploadInput, setOpenUploadInput] = useState(false)

  const template_id = searchParams.get("tid")

  const { templatesData } = useGetTemplates()

  const appTheme = useAtomValue(themeAtom)

  const easternDate = DateTime.fromISO(data.post_date, {
    zone: "America/New_York"
  }).toJSDate()

  const form = useForm<PostBodyFormType>({
    resolver: zodResolver(PostBodyFormSchema),
    defaultValues: {
      post_id: data.post_id,
      post_content: data.post_content,
      hashtags: data.tags,
      post_date: easternDate,
      post_time: data.post_time,
      image_recommendation: data.image_video_recommendation ?? "",
      catalogue: data.catalogue ?? ""
    }
  })

  useEffect(() => {
    const easternNewDate = DateTime.fromISO(data.post_date, {
      zone: "America/New_York"
    }).toJSDate()

    form.reset({
      post_id: data.post_id,
      post_content: data.post_content,
      hashtags: data.tags,
      post_date: easternNewDate,
      post_time: data.post_time,
      image_recommendation: data.image_video_recommendation ?? "",
      catalogue: data.catalogue ?? ""
    });
    // setSocialPost(data)
    setCurrentImg(data.image_url); // Also update image if needed
  }, [data]);

  const dateInput = useWatch({
    control: form.control,
    name: "post_date",
  });

  const watchedTags = useWatch({
    control: form.control,
    name: "hashtags",
  });

  const tagInputRef = useRef<HTMLInputElement | null>(null)

  const changedImageRef = useRef(false)

  const [currentImg, setCurrentImg] = useState(data.image_url);

  const [showInput, setShowInput] = useState(false)

  const [otherPlatforms, setOtherPlatforms] = useState<Platform[]>([])


  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (tagInputRef && tagInputRef.current) {
        const value = `#${tagInputRef.current.value}`;
        if (value) {
          form.setValue("hashtags", [...form.getValues("hashtags"), value], { shouldDirty: true });
          tagInputRef.current.value = "";
        }
      }
    }
  }

  const { updatePostBody } = useUpdatePostBody()

  const { saveImage } = useSaveImageTheme()

  const submitHandler = (formData: PostBodyFormType) => {
    if (data.image_ids === null) {
      saveImage({
        event_id: event_id,
        image_id: data.image_id,
        story_id: story_id ? story_id : 0
      })
    }
    posthog.capture('post edited', { post_id: formData.post_id, hasContentChanged: data.post_content === formData.post_content, content_prev: formData.post_content, content_new: data.post_content })
    updatePostBody({ ...formData, post_date: format(formData.post_date, "yyyy-MM-dd"), post_time: formData.post_time.length < 8 ? `${formData.post_time}:00` : formData.post_time })
  }

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // Reset height to auto to calculate new height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set new height based on content
    }
  }, [data]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  const direction = showPrompt ? 1 : -1;


  return (
    <div className="h-full rounded-3xl relative shadow-md shadow-gray-200">
      <GlowingEffect
        borderWidth={3}
        spread={50}
        glow={true}
        disabled={false}
        proximity={128}
        inactiveZone={0.01}
      />
      <motion.div className="w-full h-auto flex flex-col gap-3 p-5 bg-white relative rounded-3xl">
        {/* Header space */}
        <div className="flex items-start md:items-center justify-between w-full gap-2 flex-col-reverse md:flex-row">
          <div className="flex items-start flex-col lg:flex-row max-lg:gap-2">
            <h2 className="text-xl font-bold font-montserrat text-gray-600 drop-shadow-lg">{event_name}</h2>
            <p className="text-xs font-poppins tracking-wider flex items-center gap-1 lg:ps-2">Designed for <img src={platform.url} className="size-4" /></p>
          </div>
          <div className="flex items-center gap-2">
            <Popover modal>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "text-lg md:text-xl font-header flex items-center justify-center tracking-wider font-bold px-4 bg-gradient-to-tr from-blue-600 via-purple-400 to-indigo-400 text-transparent bg-clip-text border border-purple-300",
                    !dateInput && "text-muted-foreground",
                  )}
                >
                  {dateInput ? (
                    format(dateInput, "MMM d")
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateInput}
                  onSelect={(date: Date | undefined) => {
                    if (date) {
                      form.setValue("post_date", date)
                      updatePostBody({
                        post_id: data.post_id,
                        post_content: data.post_content,
                        hashtags: data.tags,
                        post_date: format(date, "yyyy-MM-dd"),
                        post_time: data.post_time,
                      })
                    }
                  }}
                  disabled={date => {
                    const today = startOfDay(new Date());
                    const currentDate = startOfDay(date);
                    return isPast(currentDate) && !isSameDay(currentDate, today);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <TimePicker
              className="font-montserrat border border-purple-300 w-fit font-medium bg-gradient-to-tr from-blue-600 via-purple-400 to-indigo-400 text-transparent bg-clip-text"
              value={form.watch("post_time")}
              onChange={(time: string) => form.setValue("post_time", time, { shouldDirty: true })}
            />
          </div>

        </div>

        <AnimatePresence initial={false} custom={direction} mode="wait">

          {/* content section */}
          {!showPrompt && <motion.div
            key="content"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              duration: 0.5,
              ease: "easeInOut"
            }}
            className="w-full flex flex-col lg:flex-row items-start gap-4">
            <div className="w-full lg:w-3/5 h-full">
              <form onSubmit={() => form.handleSubmit(submitHandler)} className="h-full rounded-xl bg-white p-3 bg-clip-padding backdrop-filter backdrop-blur-xl bg-opacity-60 border border-purple-300">
                <div className="w-full flex items-center justify-between gap-2">
                  <p className="font-poppins tracking-wide text-gray-500 h-[2rem]">Content</p>
                  {form.formState.isDirty && (
                    <div className="flex items-center gap-2">
                      <Button type="button"
                        onClick={() => form.reset({

                          post_id: data.post_id,
                          post_content: data.post_content,
                          hashtags: data.tags,
                          post_date: new Date(data.post_date),
                          post_time: data.post_time,
                          image_recommendation: data.image_video_recommendation ?? "",
                          catalogue: data.catalogue ?? ""
                        })}
                        variant="outline"
                        size="icon"
                        className="rounded-full border-2 border-purple-300"
                      ><X />
                      </Button>
                      <Button type="button"
                        disabled={!appTheme.isClient}
                        onClick={() => form.handleSubmit(submitHandler)()}
                        variant="outline"
                        size="icon"
                        className="rounded-full border-2 border-purple-300"
                      ><Check />
                      </Button>
                    </div>
                  )}
                </div>
                <textarea rows={8} {...form.register("post_content")}
                  className="w-full text-sm resize-none bg-transparent focus:border focus:border-gray-200 focus:outline-none rounded-xl p-2 text-gray-700 font-poppins tracking-wider" />
                <div className="flex flex-wrap items-center gap-2">
                  {watchedTags.map((item, index) => (
                    <span key={index} className="border border-orange-300 shadow flex items-center gap-1 shadow-orange-200 bg-white py-1 px-2 rounded-lg text-xs font-poppins tracking-wider">
                      {item}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="size-4 rounded-full text-orange-600 outline-none border-0"
                        onClick={() => {
                          const updatedTags = watchedTags.filter((_, i) => i !== index);
                          form.setValue("hashtags", updatedTags, { shouldDirty: true });
                        }}
                      >
                        <X size="5" />
                      </Button></span>
                  ))}
                  {!showInput ? (
                    <Button
                      type="button"
                      onClick={() => setShowInput(true)}
                      variant="outline"
                      size="sm"
                      className="border border-orange-300 shadow shadow-orange-200 bg-white hover:bg-orange-50 text-orange-600 hover:text-orange-800 rounded-lg h-7 w-7 p-0"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  ) : (
                    <Input
                      ref={tagInputRef}
                      onKeyDown={handleKeyDown}
                      onBlur={() => {
                        setShowInput(false)
                      }}
                      type="text"
                      placeholder="Add a tag..."
                      className="border border-orange-300 shadow flex items-center gap-1 shadow-orange-200 bg-white py-1 px-2 rounded-lg placeholder:text-xs font-poppins tracking-wider text-xs focus-visible:ring-orange-500 h-7 outline-orange-500"
                      autoFocus
                    />
                  )}
                </div>
              </form>
              <div className="relative py-2">
                <FeedbackPopover post_id={data.post_id} />
              </div>
            </div>
            <div className="max-md:mx-auto w-2/3 md:w-2/5 relative">
              {data.image_url !== null &&
                <ImageDialog
                  media_type={data.type === "video" ? "video" : "image"}
                  currentImg={currentImg}
                  image_id={data.image_id}
                  image_video_recommendation={data.image_video_recommendation}
                  galleryTrigger={

                    <GallerySelector
                      story_id={story_id === null ? 0 : story_id}
                      key={(data.image_ids ?? []).map(i => i.image_id).join(",") || data.image_id || "none"}
                      isCarousel={true}
                      event_id={event_id}
                      content={data}
                      selectedImages={data.image_ids !== null ? data.image_ids : data.image_id !== null ? [{
                        image_url: data.image_url,
                        image_id: data.image_id,
                        type: data.type
                      }] : []}
                      trigger={
                        <Button
                          type="button"
                          variant="outline"
                          title="Add more images to this post"
                          className="rounded-xl bg-opacity-70 flex-1 text-[0.6rem] md:text-xs font-poppins h-7 flex items-center gap-2 border-2 border-purple-300">
                          <Plus size={13} />Make carousel</Button>
                      }
                      onSave={(url: string) => {
                        setCurrentImg(url)
                        changedImageRef.current = true
                      }}
                    />
                  } />
              }
              {data.image_ids !== null &&
                <ImageCarousel images={data.image_ids} image={currentImage} setImage={(idx: number) => setCurrentImage(idx)} />
              }
              <div className="w-full flex items-center gap-2 flex-wrap">
                <GallerySelector
                  story_id={story_id === null ? 0 : story_id}
                  key={(data.image_ids ?? []).map(i => i.image_id).join(",") || data.image_id || "none"}
                  isCarousel={data.image_ids === null ? false : true}
                  event_id={event_id}
                  content={data}
                  selectedImages={data.image_ids !== null ? data.image_ids : data.image_id !== null ? [{
                    image_id: data.image_id,
                    image_url: data.image_url,
                    type: data.type
                  }] : []}
                  trigger={
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full flex-1 text-[0.6rem] tracking-wide md:text-xs font-poppins flex items-center gap-2 border-2 border-purple-300">
                      Change Image
                      <ImagePlus size={13} /></Button>
                  }
                  onSave={(url: string) => {
                    setCurrentImg(url)
                    changedImageRef.current = true
                  }}
                />
                {status === 6 && template_id !== null &&
                  <>
                    <EditTextDialog
                      openTextEditor={openTextEditor}
                      setOpen={(val: boolean) => setOpenTextEditor(val)}
                      key={currentImage}
                      isCarousel={data.image_ids !== null}
                      post_image_id={data.image_ids === null ? data.image_id : data.image_ids[currentImage].image_id}
                      event_id={event_id}
                      image_id={data.image_ids === null ? data.image_id : data.image_ids[currentImage].image_id}
                      image_url={data.image_ids === null ? data.image_url : data.image_ids[currentImage].image_url}
                      template_id={parseInt(template_id)}
                      trigger={
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full flex-1 text-[0.6rem] tracking-wide md:text-xs font-poppins flex items-center gap-2 border-2 border-purple-300">
                          Edit Template
                          <SquarePen size={13} className="lg:hidden" /></Button>
                      } />

                    {templatesData &&
                      <ImageUploadDialog
                        open={openUploadInput}
                        setOpen={(val: boolean) => {
                          setOpenTextEditor(val)
                          setOpenUploadInput(val)
                        }}
                        openTextEditor={openTextEditor}
                        isCarousel={data.image_ids !== null}
                        post_image_id={data.image_id !== null ? data.image_id : data.image_ids !== null && data.image_ids.length > 0 ? data.image_ids[0].image_id : 0}
                        event_id={event_id}
                        template={getTemplateDetails(parseInt(template_id), templatesData.data)}
                        trigger={
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="flex-1 rounded-full border-purple-300">
                            <Upload size={10} className="scale-75 sm:scale-100" />
                          </Button>
                        }
                      />
                    }

                  </>
                }
              </div>
            </div>
          </motion.div>
          }
          {/* prompt section */}
          {showPrompt && <motion.div
            key="prompt"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              duration: 0.5,
              ease: "easeInOut"
            }}
            className="w-full flex flex-col gap-2 h-full">
            <PromptForm setShowPrompt={setShowPrompt} event_id={event_id} story_id={story_id} />
          </motion.div>}

        </AnimatePresence>

        {/* Buttons and Avatar */}
        <div className="w-full flex items-center justify-between flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2">
            <MultiPlatformSelect
              selectedPlatforms={otherPlatforms}
              setSelectedPlatforms={setOtherPlatforms}
              currentPlatform={platform}
              platformOptions={filteredPlatforms} />
          </div>
          {!showPrompt &&
            (
              <>
                {(DateTime.fromISO(data.post_date, { zone: 'America/New_York' }).startOf('day') < DateTime.now().setZone('America/New_York').startOf('day')) ? (
                  <DateCheckDialog
                    data={data}
                    trigger={
                      <PublishButton disabled={!appTheme.isClient}
                        platform={platform} hasOtherPlatforms={otherPlatforms.length > 0} />
                    }
                  />
                ) : (
                  form.formState.isDirty ? (
                    <PublishButton
                      disabled={!appTheme.isClient}
                      onClick={() => toast.info("Content has unsaved changes", { description: "Save them or discard them with buttons above content to continue" })}
                      platform={platform} hasOtherPlatforms={otherPlatforms.length > 0} />
                  ) : (currentImg === null && data.image_ids === null) ? (
                    <PublishButton
                      disabled={!appTheme.isClient}
                      onClick={() => toast.info("Image is missing from the post", { description: "Media is an essential part of brand development, select an image to continue" })}
                      platform={platform} hasOtherPlatforms={otherPlatforms.length > 0} />
                  ) : (
                    <ConfirmScheduleDialog
                      event_id={event_id}
                      story_id={story_id}
                      image_id={data.image_id}
                      onSchedule={(text: string) => {
                        setMessage(text, `I want to schedule a post for ${event_name} on ${platform.name}`)
                        setStep("success")
                      }}
                      platform={platform.name}
                      otherPlatforms={otherPlatforms.map(data => data.name)}
                      post_id={data.post_id}
                      image_ids={data.image_ids === null ? null : data.image_ids.map(img => `${img.image_id}`)}
                      platformSelected={otherPlatforms}
                      trigger={
                        <PublishButton
                          disabled={form.formState.isDirty || (currentImg === null && data.image_ids === null) || !appTheme.isClient}
                          hasOtherPlatforms={otherPlatforms.length > 0}
                          platform={platform}
                        />
                      }
                    />
                  )
                )}
              </>
            )
          }
        </div>
      </motion.div>
    </div>
  )
}

export default ThemeDetailsCard;

